import { Button } from '@travelnest/merlin.button';
import { get } from 'lodash';
import * as React from 'react';
import { IBookingReceipt } from '../../../data/entities/event/booking';
import { ActionTypes } from '../../../entities/bookings/actions';
import { currencySymbolForCurrencyString } from '../../helpers/currency_helpers';
import { useReduxAction } from '../../helpers/hooks';
import { IFXRates } from '../../pages/property/bookings/details/helpers/helpers';
import { DateFormats, getFullDateUtc } from '../date-picker/helpers';
import { Content } from '../page';
import {
	calculateConvertedReceipt, calculateOutstanding, compareBookingReceipts, filterDeletedReceipts,
	getTotalForCurrency, IPendingBookingReceipt, paymentMethods, showFXAdjustment
} from './helpers';
import { ReceiptInputForm } from './ReceiptInputForm';
import { ReceiptTable } from './ReceiptTable';

export interface IGuestReceiptProps {
	bookingCurrency: string;
	ownerRentCurrency: string;
	guestPaymentValueInOwnerRentCurrency: number; // in owner rent currency
	guestPaymentValueInBookingCurrency: number; // in booking currency
	fxRates: IFXRates;
	receipts: IPendingBookingReceipt[];
	setReceipts: (receipts: IPendingBookingReceipt[]) => any;
}

const buildFXAdjustment = (amount: number, currency: string): IPendingBookingReceipt => ({
	currency,
	amount,
	method: 'fx movement',
	date: new Date(),
	reference: '',
	pending: true
});

export const GuestReceipt: React.FC<IGuestReceiptProps> = (props) => {
	const mismatchedCurrencies = props.bookingCurrency !== props.ownerRentCurrency;

	const transformGuestReceipts = (receipts: IBookingReceipt[], rates: IFXRates): IBookingReceipt[] => [...receipts].map((gr) => (
		{
			...gr,
			bookingCurrencyAmount: gr.currency === props.bookingCurrency ? gr.amount : undefined,
			ownerRentCurrencyAmount: gr.currency !== props.ownerRentCurrency ? calculateConvertedReceipt(gr, props.ownerRentCurrency, rates) : gr.amount,
			fxRate: gr.currency !== props.ownerRentCurrency ? get(rates[getFullDateUtc(gr.date, DateFormats.YearMonthDay)], 'fxRate') : undefined
		}
	));

	const transformedReceipts = transformGuestReceipts(props.receipts, props.fxRates);
	const suggestedFXAdjustment = calculateOutstanding(props.guestPaymentValueInOwnerRentCurrency, transformedReceipts, 'owner rent');
	const displayFXAdjustment = showFXAdjustment(
		transformedReceipts, props.guestPaymentValueInBookingCurrency, mismatchedCurrencies, suggestedFXAdjustment);
	const outstandingInBookingCurrency = calculateOutstanding(props.guestPaymentValueInBookingCurrency, transformedReceipts, 'booking');

	const baseReceipt = {
		currency: props.bookingCurrency,
		amount: calculateOutstanding(
			props.guestPaymentValueInBookingCurrency,
			transformedReceipts,
			'booking'
		),
		date: new Date(),
		method: Object.entries(paymentMethods)[0][0],
		pending: true
	} as IPendingBookingReceipt;

	const [showInput, setShowInput] = React.useState(false);
	const [guestReceipt, setGuestReceipt] = React.useState({ ...baseReceipt });

	const submitReceipt = (receipt: IPendingBookingReceipt) => {
		const added = JSON.parse(sessionStorage.getItem('added_guest_payments') || '[]');
		added.push(receipt);

		sessionStorage.setItem('added_guest_payments', JSON.stringify(added));
		props.setReceipts([...props.receipts, receipt]);
		setGuestReceipt({ ...baseReceipt });
	};

	const deleteReceipt = (targetReceipt: IPendingBookingReceipt, del: boolean = true) => {
		let deleted = JSON.parse(sessionStorage.getItem('deleted_guest_payments') || '[]');
		if (del) {
			deleted.push(targetReceipt);
		} else {
			deleted = deleted.filter((receipt: IPendingBookingReceipt) => JSON.stringify({ ...receipt, deleted: true }) !== JSON.stringify(targetReceipt));
		}
		sessionStorage.setItem('deleted_guest_payments', JSON.stringify(deleted));

		props.setReceipts(props.receipts.map(
			(receipt: IPendingBookingReceipt) => compareBookingReceipts(targetReceipt, receipt) ? { ...receipt, deleted: del } : receipt
		));
	};

	const copyReceipt = (receipt: IBookingReceipt) => {
		setGuestReceipt({ ...receipt, pending: true });
		setShowInput(true);
	};

	const clearPending = () => props.setReceipts(props.receipts.map((receipt: IPendingBookingReceipt) => ({ ...receipt, pending: false })));

	const handleFXAdjustment = () => {
		if (outstandingInBookingCurrency !== 0) {
			setShowInput(false);
			alert(
				'FX adjustment failed: the outstanding amount in the booking currency must equal to zero. Please reconcile the outstanding amount using a Custom Guest Payment (Exceptional Payment)'
			);
		} else {
			submitReceipt(
				buildFXAdjustment(
					calculateOutstanding(
						props.guestPaymentValueInOwnerRentCurrency,
						transformedReceipts,
						'owner rent'
					),
					props.ownerRentCurrency
				)
			);
		}
	};

	useReduxAction(ActionTypes.BOOKINGV2_UPDATE_RECEIVE, () => clearPending());

	return <Content type="full">
		<div className="booking-payment__header">
			<div className="booking-payment__title"><h2>Guest receipts</h2></div>
			<div className="booking-payment__title--button"><Button onClick={(e) => setShowInput(!showInput)}>
				{showInput ? 'Close' : 'Add'}</Button>
			</div>
		</div>

		{
			showInput &&
			<ReceiptInputForm
				lockCurrency={true}
				defaultCurrency={props.bookingCurrency}
				defaultAmount={props.guestPaymentValueInBookingCurrency}
				paymentMethods={paymentMethods}
				receipt={guestReceipt}
				setReceipt={setGuestReceipt}
				submitReceipt={submitReceipt}
			/>
		}

		<ReceiptTable
			receipts={transformedReceipts}
			deleteReceipt={deleteReceipt}
			copyReceipt={copyReceipt}
			extraColumns={mismatchedCurrencies ? ['Amount (Converted)', 'FX Rate'] : []}
			extraCells={(receipt) => {
				return [
					...(mismatchedCurrencies ?
						[
							receipt?.ownerRentCurrencyAmount ?
								currencySymbolForCurrencyString(props.ownerRentCurrency) + receipt.ownerRentCurrencyAmount?.toFixed(4)
								: '-',
							receipt?.fxRate ? receipt.fxRate.toFixed(6) : '-'
						]
						:
						[]
					)
				];
			}}
			metadata={{
				'Remaining': [
					outstandingInBookingCurrency !== 0 ? currencySymbolForCurrencyString(props.bookingCurrency) +
					outstandingInBookingCurrency.toFixed(4) : '-',
					currencySymbolForCurrencyString(props.ownerRentCurrency) +
					suggestedFXAdjustment.toFixed(4)

				],
				...(displayFXAdjustment ? {
					'Total Guest Receipts': currencySymbolForCurrencyString(props.ownerRentCurrency) +
						getTotalForCurrency(transformedReceipts, 'owner rent').toFixed(2),
					'Converted TBV': currencySymbolForCurrencyString(props.ownerRentCurrency) +
						props.guestPaymentValueInOwnerRentCurrency.toFixed(2),
					'FX Adjustment': currencySymbolForCurrencyString(props.ownerRentCurrency) +
						suggestedFXAdjustment
				} : {})
			}}
		/>

		{
			displayFXAdjustment &&
			<div className="booking-payment__fx-button-container">
				<Button
					disabled={filterDeletedReceipts(props.receipts).map((r: IBookingReceipt) => r.method).includes('fx movement')}
					onClick={handleFXAdjustment}
				>
					Add FX Adjustment
				</Button>
			</div>
		}
	</Content>;
};
